import { useNavigate, useLocation } from "react-router-dom";
import Article from "./Article";
import React from "react";
import useWebSocket from "react-use-websocket";
import { WS_BASE_URL } from "../axios";

const ActiveUsers = ({ statsData, onViewDetails }) => {
    const [users, setUsers] = React.useState([]);

    const location = useLocation();
    const navigate = useNavigate();

    const period = location.pathname.split("/").pop();

    const { lastJsonMessage } = useWebSocket(`${WS_BASE_URL}/${period}`, {
        shouldReconnect: () => true,
        reconnectInterval: 5000
    });

    React.useEffect(() => {
        setUsers([]);
    }, [period]);

    React.useEffect(() => {
        if (lastJsonMessage?.active) {
            setUsers(curr => [...curr, ...lastJsonMessage.active]);
        }else if (lastJsonMessage?.expired) {
            setUsers(curr => curr.filter(user => !lastJsonMessage.expired.includes(user)));
        }
    }, [lastJsonMessage]);

    const selectedArticle = statsData.find(
        article => article.link === location.pathname
    );

    const selectedArticleRef = React.useRef(null);

    React.useEffect(() => {
        if (selectedArticleRef.current) {
            selectedArticleRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, []);

    if (!selectedArticle) {
        return <div>Article not found</div>;
    }

    const formatPhoneNumber = (number) => {
        if (typeof number !== 'string') return number;
        const cleaned = number.replace(/\D/g, '');
        if (cleaned.length > 0) {
            if (cleaned.charAt(0) === '0') {
                return `+[country code] ${cleaned}`;
            } else if (cleaned.charAt(0) !== '+') {
                return `+${cleaned}`;
            }
            return `+${cleaned}`;
        }
        return number;
    };

    const formatForDisplay = (number) => {
        if (typeof number !== 'string') return number;
        const cleaned = number.replace(/\D/g, '');
        if (cleaned.length > 0) {
            if (cleaned.length > 7) {
                let countryCode = cleaned.substring(0, 3); 
                let restOfNumber = cleaned.substring(3);
                let formattedRest = '';
                for (let i = 0; i < restOfNumber.length; i += 3) {
                    formattedRest += restOfNumber.substring(i, i + 3) + ' ';
                }
                return `+${countryCode} ${formattedRest.trim()}`;
            }
        }
        if (number.charAt(0) !== '+' && number.length > 0) {
            return `+${number}`;
        }
        return number;
    };

    const downloadExcel = () => {
        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += "No,WhatsApp Number (E.164),Formatted Display\n";
        users.forEach((number, index) => {
            const formattedNumber = formatPhoneNumber(number);
            const displayNumber = formatForDisplay(number);
            csvContent += `${index + 1},${formattedNumber},${displayNumber}\n`;
        });
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${selectedArticle.title.replace(/\s+/g, '_')}_WhatsApp_Users.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/4">
                <div className="flex overflow-x-auto md:overflow-x-hidden md:overflow-y-auto md:max-h-screen md:h-screen md:pb-32 md:pr-4 md:grid md:grid-cols-1 gap-6 pb-4 md:pb-0">
                    {statsData.map((article, index) => (
                        <Article
                            key={index}
                            title={article.title}
                            value={article.value}
                            isObject={article.isObject}
                            isSelected={selectedArticle.title === article.title}
                            onViewDetails={() => onViewDetails(article)}
                            ref={selectedArticle.title === article.title ? selectedArticleRef : null}
                        />
                    ))}
                </div>
            </div>
            <div className="w-full md:w-3/4">
                <div className="rounded-lg border border-gray-200 bg-slate-100 p-6 sticky top-6">
                    <h2 className="text-2xl font-medium text-gray-900">{selectedArticle.title}</h2>
                    <p className="text-sm text-gray-500 mb-6">Details for {selectedArticle.title}</p>

                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                            <div>
                                <h3 className="text-lg font-medium text-gray-800">Active WhatsApp Users</h3>
                                <p className="text-sm text-gray-500">
                                    {users.length} active user{users.length !== 1 ? 's' : ''} currently viewing this article
                                </p>
                            </div>
                            {users && users.length > 0 && (
                                <button 
                                    onClick={downloadExcel}
                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                >
                                    Download Excel
                                </button>
                            )}
                        </div>

                        {users && users.length > 0 ? (
                            <div className="overflow-hidden">
                                <div className="max-h-80 overflow-y-auto">
                                    <table className="min-w-full">
                                        <thead className="bg-gray-50 sticky top-0 z-10">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    #
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    WhatsApp Number (E.164)
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Formatted Display
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {users.map((number, index) => (
                                                <tr key={index} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {index + 1}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {formatPhoneNumber(number)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {formatForDisplay(number)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">No active users found</div>
                        )}
                    </div>

                    <button
                        onClick={() => navigate("/dashboard")}
                        className="mt-6 inline-block text-blue-600 hover:underline"
                    >
                        ← Back to List
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ActiveUsers;